import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import showNotification from '../../../helpers/showNotification';
import HoursPageTimesheetToolbar from '../timesheet/toolbar';
import { deleteProjectHours, updateHours } from '../../../api/hours/HoursAPI';
import { ImBin } from 'react-icons/im';
import Button from '../../../components/shared/button';
import ModalWrapper from '../../../components/shared/modals/modal-wrapper';
import DeleteContent from '../../../components/shared/modals/delete-content';
import moment from 'moment/moment';

function TimesheetVertical({
    data,
    weekRange,
    isEditMode,
    setIsEditMode,
    firstAddedProject,
    projectsToSelect,
    setProjectsToSelect,
    isSavingAllowed,
    setIsSavingAllowed,
    filterDate,
    setFilterDate,
    handleUpdateTable,
    handleReset,
    userHash,
    globalUserHash,
    triggerUpdate,
    availableHours,
}) {
    const { t } = useTranslation();
    const [totalWeekHours, setTotalWeekHours] = useState(0);

    const [updatedData, setUpdatedData] = useState(
        firstAddedProject ? [...data, firstAddedProject] : data
    );

    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);

    useEffect(() => {
        setTotalWeekHours(calculateTotalHours(updatedData));
    }, [updatedData]);

    useEffect(() => {
        firstAddedProject && setUpdatedData([...data, firstAddedProject]);
    }, [firstAddedProject]);

    useEffect(() => {
        !isEditMode && setUpdatedData(data);
    }, [isEditMode]);
    useEffect(() => {
        setUpdatedData(data);
    }, [data]);

    function calculateTotalHours(dataset) {
        return weekRange?.reduce((total, day) => {
            const dateString = day.format('YYYY-MM-DD');
            const dayTotal = dataset?.reduce((acc, proj) => {
                const dayHours = proj?.days?.find(
                    (d) => d?.date === dateString
                )?.hours;
                return dayHours ? +acc + +dayHours : acc;
            }, 0);

            return total + dayTotal;
        }, 0);
    }

    function calculateProjectWeekTotalHours(projectData, date, isMonthly) {
        if (!projectData?.days?.length) {
            return 0;
        } else {
            return projectData?.days?.reduce(
                (acc, day) =>
                    day?.hours &&
                    date !== day?.date &&
                    ((moment(date).month() === moment(day?.date).month() &&
                        isMonthly) ||
                        !isMonthly)
                        ? acc + +day?.hours
                        : acc,
                0
            );
        }
    }

    function calculateAvailableHoursForWeekStart(
        initialAvailableHours,
        initialProjectData,
        date,
        isMonthly
    ) {
        let newAvailableHours = initialAvailableHours;

        if (initialProjectData?.days?.length) {
            initialProjectData?.days.forEach((day) => {
                if (
                    moment(date).month() === moment(day?.date).month() ||
                    !isMonthly
                ) {
                    newAvailableHours = newAvailableHours + day?.hours;
                }
            });
        }

        return newAvailableHours;
    }

    function handleUpdateData(
        newValue,
        project,
        date,
        type = 'hours',
        isDecimal = false
    ) {
        let error = null;
        // if hours, validate value
        if (type === 'hours') {
            if (+newValue > 24) {
                error = t('hours_page.errors.day_total_exceeded');
            }

            const month = moment(date).month() + 1;

            const isMonthly = availableHours[project]?.monthly ? true : false;

            if (availableHours && availableHours[project][month] !== null) {
                const weekHours = calculateProjectWeekTotalHours(
                    updatedData?.find((p) => p?.project === project),
                    date,
                    isMonthly
                );

                const newTotal = weekHours + +newValue;

                const newAvailableHours = calculateAvailableHoursForWeekStart(
                    availableHours[project][month],
                    data?.find((p) => p?.project === project),
                    date,
                    isMonthly
                );

                // console.log(
                //     'newTotal',
                //     newTotal,
                //     'newAvailableHours',
                //     newAvailableHours
                // );
                if (newTotal > newAvailableHours) {
                    if (newAvailableHours - weekHours < 0) {
                        error = t('hours_page.errors.hours_limit_exceeded');
                    } else {
                        error = t(
                            'hours_page.errors.day_total_exceeded_limit',
                            {
                                limit: newAvailableHours - weekHours,
                            }
                        );
                    }
                }
            }

            const dayOtherProjectslHoursArray = updatedData?.map((proj) => {
                // don`t count current project previous hours
                if (proj.project === project) {
                    return 0;
                } else {
                    const dateHours = proj.days.find(
                        (day) => day.date === date
                    );
                    return +dateHours?.hours;
                }
            });

            const dayOtherProjectslHours = dayOtherProjectslHoursArray.reduce(
                (acc, item) => (acc += +item),
                0
            );

            const dayTotalHours = +dayOtherProjectslHours + +newValue;

            if (newValue[0] === ',' || newValue.length > 4) {
                return;
            }

            if (dayTotalHours > 24) {
                error = t('hours_page.errors.day_total_exceeded');
            }
        }

        if (!isSavingAllowed && !error) {
            setIsSavingAllowed(true);
        }

        if (error) {
            setIsSavingAllowed(false);
        }

        // update data
        const updated = updatedData?.map((p) => {
            if (p?.project === project) {
                const updatedProject = {
                    ...p,
                    days: p?.days?.find((d) => d?.date === date)
                        ? p?.days?.map((d) => {
                              if (d?.date === date) {
                                  return {
                                      ...d,
                                      error: error ? error : null,
                                      [type]:
                                          type === 'hours'
                                              ? isDecimal
                                                  ? newValue
                                                  : +newValue
                                              : newValue,
                                  };
                              } else {
                                  return {
                                      ...d,
                                      error: null,
                                  };
                              }
                          })
                        : [
                              ...p?.days,
                              {
                                  date: date,
                                  error: error ? error : null,
                                  [type]: newValue,
                                  [type === 'hours' ? 'comment' : 'hours']:
                                      null,
                              },
                          ],
                };
                return updatedProject;
            } else {
                return p;
            }
        });

        setUpdatedData(updated);
    }

    function handleAddProject(projectOption) {
        setUpdatedData([
            ...updatedData,
            {
                days: [],
                project: projectOption?.value,
                projectName: projectOption?.label,
            },
        ]);
        setProjectsToSelect(
            projectsToSelect.filter((p) => p?.value !== projectOption?.value)
        );
    }

    //seding data to the API
    async function saveChanges() {
        const allProjects = updatedData;
        const formattedData = allProjects?.map((project) => {
            return {
                project: project.project,
                days: formatDays(project?.days),
            };
        });

        try {
            await updateHours(userHash, formattedData);
            triggerUpdate();
            setIsEditMode(false);
            setIsSavingAllowed(false);
        } catch (err) {
            showNotification(
                `${
                    err.response?.data ||
                    err.message ||
                    t('hours_page.errors.update_failed')
                }`
            );
        }
    }

    function formatDays(days) {
        const formattedDays = days?.map((day) => {
            const formattedDay = {
                hours: +day.hours?.toString()?.replace(',', '.'),
                comment: day?.comment,
                date: day?.date,
            };
            return formattedDay;
        });

        return formattedDays;
    }

    function handleResetWithDeleting() {
        if (projectToDelete) {
            triggerUpdate();
        }
        handleReset();
        setProjectToDelete(null);
    }


    async function handleProjectDelete() {
        setLoading(true);

        const isNew = !data?.find(
            (dataProject) => dataProject?.project === projectToDelete
        );

        if (isNew) {
            const updated = updatedData?.filter(
                (p) => projectToDelete !== p?.project
            );
            setProjectsToSelect([
                ...projectsToSelect,
                {
                    label: projectToDelete,
                    value: projectToDelete,
                },
            ]);
            setUpdatedData(updated);
            setProjectToDelete(null);
        } else {
            try {
                const res = await deleteProjectHours(
                    userHash,
                    projectToDelete,
                    {
                        params: {
                            filterDate: filterDate
                                .clone()
                                .day(1)
                                .format('YYYY-MM-DD'),
                        },
                    }
                );

                res?.status === 200 &&
                    setUpdatedData(
                        updatedData?.filter(
                            (p) => p?.project !== projectToDelete
                        )
                    );

                res?.status === 200 &&
                    setProjectsToSelect([
                        ...projectsToSelect,
                        {
                            label: projectToDelete,
                            value: projectToDelete,
                        },
                    ]);
            } catch (err) {
                showNotification(
                    `${
                        err.response?.data ||
                        err.message ||
                        t('hours_page.errors.delete_failed')
                    }`
                );
            }
        }

        setLoading(false);
    }

    return (
        <div className="flex flex-col max-h-[70vh]">
            <HoursPageTimesheetToolbar
                filterDate={filterDate}
                setFilterDate={setFilterDate}
                updateDates={handleUpdateTable}
                isEditMode={isEditMode}
                setIsEditMode={setIsEditMode}
                handleReset={handleResetWithDeleting}
                data={data}
                projectsToSelect={projectsToSelect}
                setProjectsToSelect={setProjectsToSelect}
                isSavingAllowed={isSavingAllowed}
                saveChanges={saveChanges}
                handleAddProject={handleAddProject}
                isGlobalUser={globalUserHash === userHash}
                updatedData={updatedData}
            />
            {/* Header */}
            <div className="mt-6 col-span-8 grid grid-cols-8 px-4  text-body  text-gray-700 border-b-2 border-solid border-gray-300 pb-4">
                <p className="col-span-3 font-semibold">Project</p>
                <p className="col-span-1 font-semibold">Hours</p>
                <p className="col-span-4 font-semibold">Description</p>
            </div>

            <div className="grid grid-cols-8   overflow-y-auto  ">
                {(data?.length && updatedData?.length) ||
                (isEditMode && updatedData?.length) ? (
                    weekRange?.map((day) => {
                        const dateString = day.format('YYYY-MM-DD');
                        const total = updatedData?.reduce((acc, proj) => {
                            const dayHours = proj?.days?.find(
                                (d) => d?.date === dateString
                            )?.hours;
                            return dayHours ? +acc + +dayHours : acc;
                        }, 0);

                        return (
                            <div
                                key={dateString}
                                className="col-span-8 grid grid-cols-8"
                            >
                                <div className="col-span-8  px-4 flex gap-2 text-lg  text-gray-500 pt-2 border-b border-solid border-gray-300">
                                    <div className="font-light ">
                                        {day.format('dddd')}
                                    </div>
                                    <div className="font-light">
                                        ({day.format('D.MM')})
                                    </div>
                                </div>
                                <ul className="col-span-8 ">
                                    {updatedData?.map((project) => {
                                        const dayData = project?.days?.find(
                                            (d) => d?.date === dateString
                                        );

                                        return (
                                            <li
                                                className="w-full grid grid-cols-8 px-4 py-2 text-gray-700 items-center odd:bg-white even:bg-gray-bg "
                                                key={
                                                    project?.project +
                                                    dateString
                                                }
                                            >
                                                <p className="pl-24 col-span-3">
                                                    {project?.projectName}
                                                </p>
                                                {isEditMode ? (
                                                    <div className="col-span-1 px-2 w-16 text-right flex flex-col items-center gap-1 relative">
                                                        <input
                                                            name={dateString}
                                                            type="text"
                                                            className="p-2 m-0 placeholder-gray-300 text-gray-700 relative bg-white
             rounded text-md border border-gray-300 outline-none focus:outline-none focus:ring w-14 "
                                                            onChange={(e) => {
                                                                const onlyNums =
                                                                    e.target.value
                                                                        .replace(
                                                                            ',',
                                                                            '.'
                                                                        )
                                                                        .replace(
                                                                            /[^0-9.]/g,
                                                                            ''
                                                                        )
                                                                        .replace(
                                                                            /(\..*?)\./g,
                                                                            '$1'
                                                                        );

                                                                handleUpdateData(
                                                                    onlyNums,
                                                                    project?.project,
                                                                    dateString,
                                                                    'hours',
                                                                    onlyNums.endsWith(
                                                                        '.'
                                                                    )
                                                                );
                                                            }}
                                                            value={
                                                                dayData?.hours
                                                            }
                                                            placeholder={0}
                                                        />
                                                    </div>
                                                ) : (
                                                    <p className="col-span-1 ml-2">
                                                        {dayData?.hours
                                                            ? dayData?.hours
                                                            : 0}
                                                    </p>
                                                )}
                                                {isEditMode ? (
                                                    <div className="col-span-3 px-2 w-full text-right flex flex-col  gap-1 relative">
                                                        <CommentInput
                                                            dateString={
                                                                dateString
                                                            }
                                                            handleUpdateData={
                                                                handleUpdateData
                                                            }
                                                            project={project}
                                                            dayData={dayData}
                                                        />
                                                    </div>
                                                ) : (
                                                    <p className="col-span-4 ml-2">
                                                        {dayData?.comment}
                                                    </p>
                                                )}

                                                {isEditMode ? (
                                                    <Button
                                                        extraClasses={`col-span-1 justify-self-end text-red-600 transform hover:scale-110 hover:text-red-700 disabled:opacity-50`}
                                                        onClick={() => {
                                                            setProjectToDelete(
                                                                project?.project
                                                            );
                                                            setModalOpen(true);
                                                        }}
                                                    >
                                                        <ImBin />
                                                    </Button>
                                                ) : null}
                                                {dayData?.error ? (
                                                    <small className="col-start-4 col-span-3 text-xs text-red-600 pt-1">
                                                        {dayData?.error}
                                                    </small>
                                                ) : null}
                                            </li>
                                        );
                                    })}
                                </ul>
                                <div className="col-span-8 grid grid-cols-8 px-4 pt-2">
                                    <p className="col-start-4 font-bold text-gray-700 ml-2">
                                        {+total}
                                    </p>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <p className="col-span-8 text-base py-4 text-gray-600 text-center">
                        {t('hours_page.no_data')}
                    </p>
                )}
            </div>
            {/* Footer */}
            <div className="mt-4 col-span-8 grid grid-cols-8 px-4  text-body  border-t-2 border-solid border-gray-300 py-4 text-gray-700">
                <p className="col-span-3 font-semibold  ">Total</p>
                <p className="col-start-4 font-bold ml-2 ">{+totalWeekHours}</p>
            </div>
            <ModalWrapper
                modalState={modalOpen}
                setModalState={setModalOpen}
                title="Remove project"
            >
                <DeleteContent
                    handleAction={handleProjectDelete}
                    setModalState={setModalOpen}
                >
                    <p className="text-xl text-gray-500">
                        {t('hours_page.delete_message', {
                            project: projectToDelete,
                        })}
                    </p>
                </DeleteContent>
            </ModalWrapper>
        </div>
    );
}

export default TimesheetVertical;


function CommentInput({dateString, handleUpdateData, project, dayData }) {
    const inputRef = useRef(null);

    useEffect(() => {
        const el = inputRef.current;
        if (el) {
            el.style.height = 'auto'; // Reset height
            el.style.height = el.scrollHeight + 'px'; // Set to scrollHeight
        }
    }, [dayData?.comment]);

    const handleFocus = () => {
        requestAnimationFrame(() => {
            const el = inputRef.current;
            if (el) {
                el.setSelectionRange(el.value.length, el.value.length);
                el.scrollLeft = el.scrollWidth;
            }
        });
    };
    return (
        <textarea
            ref={inputRef}
            name={dateString + 'comment'}
            type="text"
            className="p-2 m-0 placeholder-gray-300 text-gray-700 relative bg-white
rounded text-md border border-gray-300 outline-none focus:outline-none focus:ring w-full "
            onChange={(e) =>
                handleUpdateData(
                    e.target.value,
                    project?.project,
                    dateString,
                    'comment'
                )
            }
            value={dayData?.comment}
            placeholder={'Add your comment...'}
            onFocus={handleFocus}
            style={{
                overflow: 'hidden',
                resize: 'none',
                width: '100%',
            }}
            rows={1}
        />
    );

}
